import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import {
  selectBookingOrderItem,
  selectPurchaseOrderItem,
  selectPractitionerSummary,
} from 'selectors/OrdersSelectors'
import { selectBookingSummary } from 'selectors/BookingSummarySelectors'
import { useGetRoom } from 'api/Rooms'
import { get, set } from 'lodash'
import { Get, Paths } from 'type-fest'

type Values = Record<string, any>
type GetValue = <K extends Paths<Values>>(key: K, fallback?: Get<Values, K>) => Get<Values, K>
type SetValue = <K extends keyof Values>(key: K, value: Values[K]) => void
type UpdateValues = (values: Values) => void

interface TOrderDetailsContext {
  getValue: GetValue
  setValue: SetValue
  setValues: Dispatch<SetStateAction<Values>>
  updateValues: UpdateValues
  values: Values
}

export const OrderDetailsContext = createContext<TOrderDetailsContext>(
  null as unknown as TOrderDetailsContext
)

export const OrderDetailsProvider = ({ value: initialValue = {}, children }) => {
  const [values, setValues] = useState<Values>(initialValue)

  // Get the value of `key` from values, with dot notation
  const getValue = useCallback<GetValue>(
    (key, defaultValue) => get(values, key, defaultValue),
    [values]
  )

  // Set the property at `key` to `value`, with dot notation
  const setValue = useCallback<SetValue>((key, value) => {
    setValues((previous) => set({ ...previous }, key, value))
  }, [])

  // Update multiple values at once
  const updateValues = useCallback<UpdateValues>((updateValues) => {
    setValues((previous) => {
      const next = { ...previous }

      for (let key in updateValues) {
        set(next, key, updateValues[key])
      }

      return next
    })
  }, [])

  const value = useMemo<TOrderDetailsContext>(
    () => ({ values, setValues, getValue, setValue, updateValues }),
    [getValue, setValue, updateValues, values]
  )

  return <OrderDetailsContext.Provider value={value}>{children}</OrderDetailsContext.Provider>
}

export const useOrderDetails = () => useContext(OrderDetailsContext)

// @ts-expect-error
export const BookingOrderItemDetailsContext = createContext()

const BookingOrderItemDetailsProvider = ({ itemId, children }) => {
  const value = useProvideBookingOrderItemDetails(itemId)

  return (
    <BookingOrderItemDetailsContext.Provider value={value}>
      {children}
    </BookingOrderItemDetailsContext.Provider>
  )
}

const useProvideBookingOrderItemDetails = (itemId: string) => {
  const item = useSelector(selectBookingOrderItem(itemId))
  const { booking_summary: bookingSummaryId } = item
  const bookingSummary = useSelector(selectBookingSummary(bookingSummaryId))
  const { practitioner: practitionerId, room: roomId } = bookingSummary
  const practitioner = useSelector(selectPractitionerSummary(practitionerId))
  const { data: { data: room = {} } = {} } = useGetRoom(roomId, {
    enabled: !!roomId,
  })

  return useMemo(
    () => ({ item, bookingSummary, practitioner, room }),
    [bookingSummary, item, practitioner, room]
  )
}

export const withBookingOrderItemDetails =
  (Component) =>
  ({ itemId, ...props }) => {
    return (
      <BookingOrderItemDetailsProvider itemId={itemId}>
        <Component {...props} />
      </BookingOrderItemDetailsProvider>
    )
  }

// @ts-expect-error
export const PurchaseOrderItemDetailsContext = createContext()

export const PurchaseOrderItemDetailsProvider = ({ itemId, children }) => {
  const value = useProvidePurchaseOrderItemDetails(itemId)

  return (
    <PurchaseOrderItemDetailsContext.Provider value={value}>
      {children}
    </PurchaseOrderItemDetailsContext.Provider>
  )
}

const useProvidePurchaseOrderItemDetails = (itemId) => {
  const item = useSelector(selectPurchaseOrderItem(itemId))

  return { item }
}

export const withPurchaseOrderItemDetails =
  (Component) =>
  ({ itemId, ...props }) => {
    return (
      <PurchaseOrderItemDetailsProvider itemId={itemId}>
        <Component {...props} />
      </PurchaseOrderItemDetailsProvider>
    )
  }
